import React, { useCallback, FC, useMemo, useState } from 'react';
import { Button, Table } from 'src/common';
import { useTranslation, Trans } from 'react-i18next';
import styles from './index.module.scss';
import { ColumnProps } from 'antd/lib/table';
import classNames from 'classnames';
import { SortOrder, TableRowSelection } from 'antd/lib/table/interface';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import { faAngleDown } from '@fortawesome/pro-solid-svg-icons';
import { useGetOrganisationsByIds } from 'src/models/organisations/hooks';
import SupplierStatusDropdown from '../SupplierStatusDropdown';
import { SupplierOrg, SupplierString } from 'src/models/procurements/Tenders/types';
import { AwardStatus, SearchLogic } from '@tendium/prom-types';

const VISIBLE_ITEMS_COUNT = 7;

interface Props {
  supplierOrgIds: SupplierOrg[];
  supplierSearchStrings: SupplierString[];
  lastAddedOrgIds: string[];
  removeOrgs: (updatingIds: SupplierOrg[], updatingSearchStrings: SupplierString[], onComplete: () => void) => void;
}

interface TableDataProps {
  key: string;
  value: string | null;
  status: AwardStatus | undefined;
  type: SearchLogic;
}

function useTableColumns(onDeleteOrgs: () => void, selectedIds: string[]): ColumnProps<TableDataProps>[] {
  const { t } = useTranslation();
  return useMemo(() => {
    return [
      {
        title: t('ExpiringContracts.suppliers'),
        dataIndex: 'value',
        key: 'value',
        ellipsis: true,
        className: styles.colOrg,
        sorter: (a, b) => (a.value && b.value ? a.value.localeCompare(b.value, 'sv') : -1),
        sortDirections: ['descend', 'ascend'],
        defaultSortOrder: 'ascend' as SortOrder,
        render: (_, record) => {
          return record.value ? (
            <span className={styles.orgName}>{record.value}</span>
          ) : (
            <>
              {!record.value ? <FontAwesomeIcon icon={faExclamationTriangle} /> : null}
              {t('Common.notFound')}
            </>
          );
        }
      },
      {
        dataIndex: 'actions',
        key: 'actions',
        className: styles.orgName,
        render: (_, record) => {
          return record.key && record.type === SearchLogic.ExactMatch ? <span>{record.key}</span> : null;
        }
      },
      {
        title: t('ExpiringContracts.supplierStatus'),
        dataIndex: 'supplierStatus',
        key: 'supplierStatus',
        ellipsis: true,
        className: styles.colSupp,
        render: (_, record) => {
          return <SupplierStatusDropdown statusKey={record.key} value={record.status ?? AwardStatus.All} />;
        }
      },
      {
        title: () => {
          return (
            <Button
              type={'text'}
              onClick={onDeleteOrgs}
              disabled={!selectedIds.length}
              icon={<FontAwesomeIcon icon={faTrash} />}
            />
          );
        },
        dataIndex: 'actions',
        key: 'actions',
        className: styles.colActions
      }
    ];
  }, [onDeleteOrgs, selectedIds.length, t]);
}

export const SuppliersList: FC<Props> = props => {
  const { supplierOrgIds, removeOrgs, supplierSearchStrings, lastAddedOrgIds } = props;
  const { t } = useTranslation();

  const orgNumbers = useMemo(() => {
    return supplierOrgIds.map(supplierOrgId => supplierOrgId.orgId);
  }, [supplierOrgIds]);

  const { data: organisations, loading: organisationsLoading } = useGetOrganisationsByIds(orgNumbers);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const resetSelectedCompanies = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const [isVisible, setIsVisible] = useState(false);
  const toggleVisible = useCallback(() => {
    setIsVisible(!isVisible);
  }, [isVisible]);

  const companies = useMemo(() => {
    if (organisationsLoading && organisations.length === 0) {
      return {
        previouslyAdded: [],
        lastAdded: [],
        notFound: []
      };
    }

    const organisationsMap = new Map(organisations.map(org => [org.organisationNumber, org.organisationName]));
    const lastAddedIdsSet = new Set<string>(lastAddedOrgIds);
    const previouslyAddedSet = new Set<TableDataProps>();
    const lastAddedSet = new Set<TableDataProps>();
    const notFoundSet = new Set<TableDataProps>();

    orgNumbers.forEach(org => {
      const organisation = organisationsMap.get(org);
      if (organisation) {
        if (lastAddedIdsSet.has(org)) {
          lastAddedSet.add({
            key: org,
            value: organisation,
            status: supplierOrgIds.find(supplierOrgId => supplierOrgId.orgId === org)?.status,
            type: SearchLogic.ExactMatch
          });
        } else {
          previouslyAddedSet.add({
            key: org,
            value: organisation,
            status: supplierOrgIds.find(supplierOrgId => supplierOrgId.orgId === org)?.status,
            type: SearchLogic.ExactMatch
          });
        }
      } else {
        notFoundSet.add({ key: org, value: null, status: undefined, type: SearchLogic.ExactMatch });
      }
    });

    return {
      previouslyAdded: [...previouslyAddedSet],
      lastAdded: [...lastAddedSet],
      notFound: [...notFoundSet]
    };
  }, [organisationsLoading, organisations, lastAddedOrgIds, orgNumbers, supplierOrgIds]);

  const searchStrings = useMemo(() => {
    return supplierSearchStrings.map(searchString => ({
      key: `${searchString.orgName}-prefixsearch`,
      value: searchString.orgName,
      status: searchString.status,
      type: SearchLogic.PrefixMatch
    }));
  }, [supplierSearchStrings]);

  const companiesData: TableDataProps[] = useMemo(() => {
    return [...companies.lastAdded, ...searchStrings, ...companies.previouslyAdded, ...companies.notFound];
  }, [companies, searchStrings]);

  const companiesDataLength = useMemo(() => {
    return companiesData.length;
  }, [companiesData.length]);

  const isContentHidden = useMemo(() => {
    return companiesData.length > VISIBLE_ITEMS_COUNT;
  }, [companiesData.length]);

  const data = useMemo(() => {
    return !isContentHidden || isVisible ? companiesData : companiesData.slice(0, VISIBLE_ITEMS_COUNT);
  }, [companiesData, isContentHidden, isVisible]);

  const onDeleteOrgs = useCallback(() => {
    const selectedIdsSet = new Set(selectedIds);
    const unselectedIdsSet = new Set<string>();
    const unselectedSearchStringsSet = new Set<string>();

    companiesData.forEach(company => {
      if (selectedIdsSet.has(company.key)) {
        return;
      }

      if (company.type === SearchLogic.ExactMatch) {
        unselectedIdsSet.add(company.key);
      } else {
        const searchString = company.key.split('-')[0];
        unselectedSearchStringsSet.add(searchString);
      }
    });

    const unselectedSupplierOrgs = Array.from(unselectedIdsSet)
      .map(orgId => supplierOrgIds.find(org => org.orgId === orgId))
      .filter(Boolean) as SupplierOrg[];

    const unselectedSupplierStrings = Array.from(unselectedSearchStringsSet)
      .map(searchString => supplierSearchStrings.find(supplier => supplier.orgName === searchString))
      .filter(Boolean) as SupplierString[];

    removeOrgs(unselectedSupplierOrgs, unselectedSupplierStrings, resetSelectedCompanies);
  }, [companiesData, removeOrgs, resetSelectedCompanies, selectedIds, supplierOrgIds, supplierSearchStrings]);

  const columns: ColumnProps<TableDataProps>[] = useTableColumns(onDeleteOrgs, selectedIds);

  const onSelectChange = useCallback(selectedKeys => {
    setSelectedIds(selectedKeys);
  }, []);

  const rowSelectionConfig: TableRowSelection<TableDataProps> = {
    onChange: onSelectChange,
    preserveSelectedRowKeys: false,
    selectedRowKeys: selectedIds
  };

  return companiesData && companiesData?.length !== 0 ? (
    <div className={styles.companiesList}>
      {!!companies.notFound.length && !organisationsLoading && (
        <p className={styles.note}>
          <Trans i18nKey={'Tenders.companiesFilterWarning'} count={companies.notFound.length} />
        </p>
      )}
      <Table
        loading={organisationsLoading}
        columns={columns}
        dataSource={data}
        rowKey={'key'}
        rowClassName={styles.row}
        className={classNames(styles.table, { [styles.isNote]: !!companies.notFound.length && !organisationsLoading })}
        rowSelection={rowSelectionConfig}
      />
      {isContentHidden && (
        <button className={styles.expandAction} onClick={toggleVisible}>
          {t(isVisible ? 'Common.showLess' : 'Tenders.BuyersFilter.showMoreItemsCount', {
            count: companiesDataLength - VISIBLE_ITEMS_COUNT
          })}
          <FontAwesomeIcon
            icon={faAngleDown}
            className={classNames(styles.expandActionIcon, { [styles.isExpanded]: isVisible })}
          />
        </button>
      )}
    </div>
  ) : null;
};

export default SuppliersList;
